import * as React from 'react';
import { MenuItemData, nestedMenuItemsFromObject } from 'mui-nested-menu';
import Menu, { MenuProps } from '@mui/material/Menu';
import { forwardRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { LibraryVideo } from '../videos.domain';
import { ChangeThumbnailModal } from './ChangeThumbnailModal';
import { ChangeNameModal } from './ChangeNameModal';
import { useEditVideoMutation } from 'generated/graphql';
import { toast } from 'react-hot-toast';
import { PATHS, navigate } from '@client/routes';
import { videoToMedia } from '../videos.helpers';

interface NestedDropdownProps {
  children?: React.ReactNode;
  menuItemsData?: MenuItemData;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  MenuProps?: MenuProps;
}

export const NestedDropdown = forwardRef<
  HTMLDivElement | null,
  NestedDropdownProps
>(function NestedDropdown(props, ref) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const { menuItemsData: data, onClick, MenuProps, ...rest } = props;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
    onClick && onClick(e);
  };
  const handleClose = () => setAnchorEl(null);

  const menuItems = nestedMenuItemsFromObject({
    handleClose,
    isOpen: open,
    menuItemsData: data?.items ?? [],
  });

  return (
    <div ref={ref} {...rest}>
      <div
        className="bg-gray-400 bg-opacity-50 cursor-pointer w-[34px] h-[20px] justify-center rounded-md flex items-center"
        onClick={handleClick}
      >
        <div>
          <FontAwesomeIcon icon={faEllipsis} color="white" />
        </div>
      </div>
      <Menu
        {...MenuProps}
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
      >
        {menuItems}
      </Menu>
    </div>
  );
});

const createMenuItemsData = (
  media: LibraryVideo,
  {
    onChangeThumbnail,
    onChangeName,
    onCreateExercise,
  }: {
    onChangeThumbnail: () => void;
    onChangeName: () => void;
    onCreateExercise: () => void;
  },
  display: {
    changeName: boolean;
    changeThumbnail: boolean;
    createExercise: boolean;
  },
) => {
  const menuItemsData: MenuItemData = {
    label: '...',
    items: [
      display.changeName && {
        label: 'Change Name',
        callback: (_event: unknown, _item: unknown) => onChangeName(),
      },
      display.changeThumbnail && {
        label: 'Change thumbnail',
        callback: (_event: unknown, _item: unknown) => onChangeThumbnail(),
      },
      display.createExercise && {
        label: 'Create exercise',
        callback: (_event: unknown, _item: unknown) => onCreateExercise(),
      },
    ].filter((x) => x) as any,
  };

  return menuItemsData;
};

type VideoMenuProps = {
  video: LibraryVideo;
  className?: string;
  display?: {
    changeName: boolean;
    changeThumbnail: boolean;
    createExercise: boolean;
  };
};
export function VideoMenu(props: VideoMenuProps) {
  const display = {
    changeName: true,
    changeThumbnail: true,
    createExercise: true,
    ...props.display,
  };
  const [showThumbnailModal, setShowThumbnailModal] = React.useState(false);
  const [showNameModal, setShowNameModal] = React.useState(false);

  const menuItemsData = React.useMemo(() => {
    return createMenuItemsData(
      props.video,
      {
        onChangeThumbnail: () => setShowThumbnailModal(true),
        onChangeName: () => setShowNameModal(true),
        onCreateExercise: () => {
          navigate(
            PATHS.SINGLE_EXERCISE_CREATE(props.video.creatorId, {
              video: props.video,
            }),
          );
        },
      },
      display,
    );
  }, [props.video, props.display]);
  const [editVideo] = useEditVideoMutation();

  const changeName = (name: string) => {
    editVideo({
      variables: {
        creatorId: props.video.creatorId,
        id: String(props.video.id),
        name,
        videoSourceType: '', // TODO check
      },
    }).then(() => {
      toast.success('Name changed successfully');
    });
  };

  const changeThumbnail = (thumbnailTimestamp: number) => {
    return editVideo({
      variables: {
        creatorId: props.video.creatorId,
        id: String(props.video.id),
        thumbnailTimestamp,
        videoSourceType: '', // TODO check
      },
    }).then(() => {
      toast.success('Thumbnail changed successfully');
    });
  };

  return (
    <div>
      <ChangeThumbnailModal
        media={props.video}
        isOpen={showThumbnailModal}
        onClose={() => setShowThumbnailModal(false)}
        onSave={(timeInSeconds) => changeThumbnail(timeInSeconds)}
      />
      <ChangeNameModal
        media={props.video}
        isOpen={showNameModal}
        onClose={() => setShowNameModal(false)}
        onSave={(name) => changeName(name)}
      />
      <NestedDropdown {...props} menuItemsData={menuItemsData} />
    </div>
  );
}
